import React, { useState, useEffect } from 'react';
import { FiX, FiCode, FiDownload, FiCopy, FiCheck, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import { generateTerraform, type TerraformFiles } from '../../api/ai';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  projectName?: string;
}

type TabKey = 'main' | 'variables' | 'outputs';

const TABS: { key: TabKey; label: string; filename: string }[] = [
  { key: 'main',      label: 'main.tf',      filename: 'main.tf' },
  { key: 'variables', label: 'variables.tf',  filename: 'variables.tf' },
  { key: 'outputs',   label: 'outputs.tf',    filename: 'outputs.tf' },
];

const TerraformModal: React.FC<Props> = ({ isOpen, onClose, nodes, edges, provider, environment, projectName }) => {
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [terraform, setTerraform] = useState<TerraformFiles | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('main');
  const [copied, setCopied]       = useState(false);
  const [metadata, setMetadata]   = useState<{ durationMs: number; tokensUsed: number } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTerraform(null);
    setError(null);
    setActiveTab('main');
    generate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await generateTerraform({ nodes, edges, provider, environment });
      setTerraform(result.terraform);
      setMetadata(result.metadata);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!terraform) return;
    await navigator.clipboard.writeText(terraform[activeTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (key: TabKey) => {
    if (!terraform) return;
    const tab = TABS.find(t => t.key === key)!;
    const blob = new Blob([terraform[key]], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = tab.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleDownloadAll = () => {
    TABS.forEach(t => handleDownload(t.key));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-purple-600 rounded-xl flex items-center justify-center">
              <FiCode className="text-white text-lg" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Terraform HCL</h2>
              <p className="text-xs text-gray-500">
                {projectName ? `${projectName} — ` : ''}{provider} · {environment}
                {metadata && ` · ${(metadata.durationMs / 1000).toFixed(1)}s · ${metadata.tokensUsed.toLocaleString()} tokens`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {terraform && (
              <button
                onClick={handleDownloadAll}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FiDownload size={14} />
                <span>Tout télécharger</span>
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <FiX />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {loading && (
            <div className="flex-1 flex flex-col items-center justify-center space-y-4 py-16">
              <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
                <FiRefreshCw className="animate-spin text-purple-600 text-xl" />
              </div>
              <div className="text-center">
                <p className="font-medium text-gray-800">Génération du Terraform en cours…</p>
                <p className="text-sm text-gray-500 mt-1">Claude analyse l'architecture et génère le HCL</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center space-x-2 text-red-600">
                  <FiAlertCircle size={20} />
                  <span className="font-medium">Erreur de génération</span>
                </div>
                <p className="text-sm text-gray-600">{error}</p>
                <button
                  onClick={generate}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm hover:bg-purple-700 transition-colors mx-auto"
                >
                  <FiRefreshCw size={14} />
                  <span>Réessayer</span>
                </button>
              </div>
            </div>
          )}

          {terraform && !loading && (
            <>
              {/* Tabs */}
              <div className="flex items-center space-x-1 px-6 pt-4 pb-0 flex-shrink-0">
                {TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-purple-600 text-purple-700 bg-purple-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
                <div className="flex-1" />
                {/* Tab actions */}
                <div className="flex items-center space-x-1 pb-1">
                  <button
                    onClick={handleCopy}
                    className="flex items-center space-x-1.5 px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {copied ? <FiCheck size={13} className="text-green-500" /> : <FiCopy size={13} />}
                    <span>{copied ? 'Copié !' : 'Copier'}</span>
                  </button>
                  <button
                    onClick={() => handleDownload(activeTab)}
                    className="flex items-center space-x-1.5 px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiDownload size={13} />
                    <span>{TABS.find(t => t.key === activeTab)?.filename}</span>
                  </button>
                </div>
              </div>

              {/* Code */}
              <div className="flex-1 overflow-auto mx-6 mb-6 mt-2 min-h-0">
                <pre
                  className="text-xs leading-relaxed h-full overflow-auto rounded-xl border border-gray-200"
                  style={{
                    background: '#1e1e2e',
                    color: '#cdd6f4',
                    padding: '20px',
                    fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
                    whiteSpace: 'pre',
                    tabSize: 2,
                  }}
                >
                  {terraform[activeTab] || '# (fichier vide)'}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerraformModal;
